import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { developerSnippets } from '@/mocks/homepageData';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const languageColors: Record<string, { bg: string; text: string }> = {
  python: { bg: 'bg-[#00ff88]/10', text: 'text-[#00ff88]' },
  javascript: { bg: 'bg-[#ffaa00]/10', text: 'text-[#ffaa00]' },
};

export default function DeveloperSection() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });
  const [activeTab, setActiveTab] = useState(0);

  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#999]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#666]' : 'text-[#888]';
  const bgWhite5 = isDark ? 'bg-white/5' : 'bg-black/5';
  const bgWhite10 = isDark ? 'bg-white/10' : 'bg-black/10';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBgWhite5 = isDark ? 'hover:bg-white/5' : 'hover:bg-black/5';
  const hoverTextWhite = isDark ? 'hover:text-white' : 'hover:text-[#111]';

  const codeTextColor = isDark ? 'text-[#ccc]' : 'text-[#444]';
  const lineNumberColor = isDark ? 'text-[#444]' : 'text-[#bbb]';
  const syntaxPink = isDark ? 'text-[#ff79c6]' : 'text-[#d73a49]';
  const syntaxYellow = isDark ? 'text-[#f1fa8c]' : 'text-[#6f42c1]';
  const syntaxBlue = isDark ? 'text-[#8be9fd]' : 'text-[#005cc5]';
  const syntaxComment = isDark ? 'text-[#6272a4]' : 'text-[#6a737d]';
  const syntaxGreen = isDark ? 'text-[#50fa7b]' : 'text-[#22863a]';

  const sdkItems = [
    { label: t('dev.restApi'), icon: 'ri-global-line' },
    { label: t('dev.websocket'), icon: 'ri-broadcast-line' },
    { label: t('dev.pythonSdk'), icon: 'ri-code-s-slash-line' },
    { label: t('dev.agentSdk'), icon: 'ri-robot-2-line' },
    { label: t('dev.tradingSdk'), icon: 'ri-line-chart-line' },
  ];

  return (
    <section
      id="developers"
      ref={sectionRef}
      className={`relative w-full py-16 md:py-24 border-t ${borderLight}`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 md:mb-14"
        >
          <span className="text-xs text-[#00ff88] font-mono uppercase tracking-widest">{t('dev.developerFirst')}</span>
          <h2 className={`mt-3 text-2xl md:text-3xl lg:text-4xl font-bold ${textPrimary}`}>
            {t('dev.builtForBuilders')}
          </h2>
          <p className={`mt-3 text-sm md:text-base max-w-2xl mx-auto ${textSecondary}`}>
            {t('dev.description')}
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left - SDK List */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:w-1/3"
          >
            <div className="glass-card p-5">
              <h3 className={`text-sm font-semibold mb-4 uppercase tracking-wider ${textPrimary}`}>{t('dev.sdksAndApis')}</h3>
              <div className="space-y-2">
                {sdkItems.map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group cursor-pointer ${hoverBgWhite5}`}
                  >
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${bgWhite5} group-hover:bg-[#00ff88]/10`}>
                      <i className={`${item.icon} w-4 h-4 flex items-center justify-center transition-colors ${textMuted} group-hover:text-[#00ff88]`} />
                    </div>
                    <span className={`text-sm transition-colors ${textSecondary} ${hoverTextWhite}`}>
                      {item.label}
                    </span>
                    <i className={`ri-arrow-right-line w-4 h-4 flex items-center justify-center ml-auto opacity-0 group-hover:opacity-100 transition-opacity ${textMuted}`} />
                  </motion.div>
                ))}
              </div>

              <div className={`mt-5 pt-4 border-t ${borderLight}`}>
                <a
                  href="#"
                  className="inline-flex items-center gap-2 text-xs text-[#00ff88] hover:text-[#00cc6a] transition-colors font-mono"
                >
                  <i className="ri-external-link-line w-3 h-3 flex items-center justify-center" />
                  {t('dev.viewDocs')}
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right - Code Snippets */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="lg:w-2/3"
          >
            <div className="code-block p-5 h-full">
              {/* Tabs */}
              <div className={`flex items-center gap-1 mb-4 border-b pb-3 ${borderLight}`}>
                {developerSnippets.map((snippet, index) => (
                  <button
                    key={snippet.label}
                    onClick={() => setActiveTab(index)}
                    className={`px-3 py-1.5 text-xs font-mono rounded-md transition-all whitespace-nowrap ${
                      activeTab === index
                        ? `${bgWhite10} ${textPrimary}`
                        : `${textMuted} ${isDark ? 'hover:text-[#999]' : 'hover:text-[#666]'}`
                    }`}
                  >
                    {t(`dev.${snippet.language === 'python' ? 'pythonSdk' : snippet.label === 'REST API' ? 'restApi' : 'websocket'}`, snippet.label)}
                  </button>
                ))}
              </div>

              {/* Code Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-mono ${
                        languageColors[developerSnippets[activeTab].language]?.bg || bgWhite5
                      } ${
                        languageColors[developerSnippets[activeTab].language]?.text || textMuted
                      }`}
                    >
                      {developerSnippets[activeTab].language}
                    </span>
                    <span className={`text-[10px] font-mono ${isDark ? 'text-[#444]' : 'text-[#bbb]'}`}>
                      example.{developerSnippets[activeTab].language === 'python' ? 'py' : 'js'}
                    </span>
                  </div>
                  <pre className="text-xs md:text-sm font-mono leading-relaxed overflow-x-auto">
                    <code className={codeTextColor}>
                      {developerSnippets[activeTab].code.split('\n').map((line, i) => {
                        let styledLine = line;
                        if (line.includes('import') || line.includes('from') || line.includes('const')) {
                          styledLine = line.replace(/(import|from|const|let|var)/g, `<span class="${syntaxPink}">$1</span>`);
                        }
                        if (line.includes('"') || line.includes("'")) {
                          styledLine = styledLine.replace(/(["'])(.*?)(\1)/g, `<span class="${syntaxYellow}">$1$2$3</span>`);
                        }
                        if (line.includes('//')) {
                          styledLine = styledLine.replace(/(\/\/.*)/g, `<span class="${syntaxComment}">$1</span>`);
                        }
                        if (line.includes('await') || line.includes('async') || line.includes('new')) {
                          styledLine = styledLine.replace(/(await|async|new|function|class|return)/g, `<span class="${syntaxBlue}">$1</span>`);
                        }
                        if (line.includes('(') && line.includes(')')) {
                          styledLine = styledLine.replace(/(\w+)(\()/g, `<span class="${syntaxGreen}">$1</span>$2`);
                        }
                        return (
                          <div key={i} className="flex">
                            <span className={`select-none w-6 text-right mr-3 flex-shrink-0 ${lineNumberColor}`}>
                              {i + 1}
                            </span>
                            <span dangerouslySetInnerHTML={{ __html: styledLine || '&nbsp;' }} />
                          </div>
                        );
                      })}
                    </code>
                  </pre>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}